// Pure helpers: date ranges, API filter mapping, formatting, CSV / PNG export.
import html2canvas from "html2canvas";
import { DEFAULT_FILTERS, FILTER_KEYS, PERIOD_PRESETS } from "./constants";

export const pad = (n) => String(n).padStart(2, "0");

export const toISO = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const monthRange = (startYear, startMonth0, endYear, endMonth0) => ({
  startDate: toISO(new Date(startYear, startMonth0, 1)),
  endDate: toISO(new Date(endYear, endMonth0 + 1, 0)),
});

// Every preset is aligned to whole calendar months so period comparisons are like-for-like.
export const computeDateRange = (filters, now = new Date()) => {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (filters.period) {
    case "3m":
      return monthRange(y, m - 2, y, m);
    case "6m":
      return monthRange(y, m - 5, y, m);
    case "12m":
      return monthRange(y, m - 11, y, m);
    case "ytd":
      return monthRange(y, 0, y, m);
    case "last-year":
      return monthRange(y - 1, 0, y - 1, 11);
    case "custom": {
      let { from, to } = filters;
      if (from && to && from > to) [from, to] = [to, from];
      const range = {};
      if (from) {
        const [fy, fm] = from.split("-").map(Number);
        range.startDate = toISO(new Date(fy, fm - 1, 1));
      }
      if (to) {
        const [ty, tm] = to.split("-").map(Number);
        range.endDate = toISO(new Date(ty, tm, 0));
      }
      return range;
    }
    default:
      return {};
  }
};

export const toApiFilters = (filters) => {
  const range = computeDateRange(filters);
  let minRating = null;
  let maxRating = null;
  if (filters.rating) {
    const [a, b] = filters.rating.split("-").map(Number);
    minRating = a;
    maxRating = b || a;
  }
  const bool = (v) => (v === "true" ? true : v === "false" ? false : null);
  return {
    ...range,
    department: filters.department,
    sentiment: filters.sentiment,
    minRating,
    maxRating,
    employee: filters.employee,
    topic: filters.topic,
    theme: filters.theme,
    themeSide: filters.themeSide,
    strength: filters.strength,
    weakness: filters.weakness,
    product: filters.product,
    urgency: filters.urgency,
    hasText: bool(filters.hasText),
    hasReply: bool(filters.hasReply),
    search: filters.search.trim(),
  };
};

export const parseFilters = (params) => {
  const filters = { ...DEFAULT_FILTERS };
  FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value !== null) filters[key] = value;
  });
  if (!PERIOD_PRESETS.some((p) => p.key === filters.period))
    filters.period = DEFAULT_FILTERS.period;
  return filters;
};

export const nf = (value, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "–";
  return Number(value).toLocaleString("pl-PL", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const pct = (part, whole) => (whole ? (part / whole) * 100 : 0);

export const dateFormatter = new Intl.DateTimeFormat("pl-PL", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

export const formatDate = (iso) =>
  iso ? dateFormatter.format(new Date(`${iso}T00:00:00Z`)) : "–";

export const formatRange = (start, end) => `${formatDate(start)} – ${formatDate(end)}`;

export const isAbortError = (err) =>
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED" ||
  err?.name === "AbortError";

export const errorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (err?.response?.status === 403) return "Brak uprawnień do analizy opinii.";
  return err?.message || "Nie udało się pobrać danych.";
};

export const plural = (n, one, few, many) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};

export const reviewsWord = (n) => plural(n, "opinia", "opinie", "opinii");

export const resolveGranularity = (granularity, range) => {
  if (granularity !== "auto") return granularity;
  if (!range.startDate || !range.endDate) return "quarter";
  const months =
    (new Date(range.endDate) - new Date(range.startDate)) /
    (1000 * 60 * 60 * 24 * 30.4);
  return months > 30 ? "quarter" : "month";
};

export const exportElement = async (elementId, filename) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  const canvas = await html2canvas(element, {
    backgroundColor: "#ffffff",
    scale: 2,
    ignoreElements: (el) => el.dataset?.exportIgnore === "true",
  });
  const link = document.createElement("a");
  link.download = `${filename}.png`;
  link.href = canvas.toDataURL();
  link.click();
};

// Scroll an element into view just below the sticky filter bar (only if it is above the fold).
export const scrollBelowSticky = (element) => {
  if (!element) return;
  const sticky = document.querySelector(".gr-sticky");
  const offset = (sticky ? sticky.getBoundingClientRect().height : 0) + 8;
  if (element.getBoundingClientRect().top >= offset) return;
  element.style.scrollMarginTop = `${offset}px`;
  element.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const csvCell = (value) => {
  let text = value === null || value === undefined ? "" : String(value);
  // Review text is user content: stop Excel from evaluating cells such as "=HYPERLINK(...)".
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
